import { formatTableCellValue } from '@/lib/tableValue';
import { normalizeImageUrl } from '@/lib/imageProxy';

export type PreviewCellKind = 'url' | 'number' | 'text' | 'empty' | 'image';

const URL_COLUMN_PATTERN = /(url|uri|href|link|src|image)/i;
const DOMAIN_PATTERN = /^[a-z0-9.-]+\.[a-z]{2,}(?::\d+)?(?:\/|$|\?)/i;
const NUMERIC_PATTERN = /^[+-]?(?:\d+|\d{1,3}(?:,\d{3})+)(?:\.\d+)?$/;
const NUMERIC_WITH_WRAPPERS_PATTERN = /^[($€£¥\s]*[+-]?(?:\d+|\d{1,3}(?:,\d{3})+)(?:\.\d+)?[%)]*$/;

const COMPACT_TEXT_MAX_LENGTH = 180;
const COMPACT_URL_MAX_LENGTH = 96;
const EXPAND_TEXT_MIN_LENGTH = 120;
const EXPAND_URL_MIN_LENGTH = 64;
const EXPAND_UNBROKEN_TOKEN_MIN_LENGTH = 40;

const truncateMiddle = (value: string, maxLength: number): string => {
  if (value.length <= maxLength) {
    return value;
  }
  const head = Math.ceil((maxLength - 1) / 2);
  const tail = Math.floor((maxLength - 1) / 2);
  return `${value.slice(0, head)}…${value.slice(value.length - tail)}`;
};

const compactUrlValue = (value: string): string => {
  const trimmed = value.trim();
  if (!trimmed) {
    return '—';
  }

  if (trimmed.startsWith('/')) {
    return truncateMiddle(trimmed, COMPACT_URL_MAX_LENGTH);
  }

  const withProtocol = /^[a-z]+:\/\//i.test(trimmed);
  const maybeAbsolute = withProtocol || DOMAIN_PATTERN.test(trimmed);

  if (!maybeAbsolute) {
    return truncateMiddle(trimmed, COMPACT_URL_MAX_LENGTH);
  }

  try {
    const parsed = new URL(withProtocol ? trimmed : `https://${trimmed}`);
    const pathname = parsed.pathname === '/' ? '' : parsed.pathname;
    const queryMarker = parsed.search ? '?…' : '';
    const hashMarker = parsed.hash ? '#…' : '';
    return truncateMiddle(`${parsed.hostname}${pathname}${queryMarker}${hashMarker}`, COMPACT_URL_MAX_LENGTH);
  } catch {
    return truncateMiddle(trimmed, COMPACT_URL_MAX_LENGTH);
  }
};

const isNumericString = (value: string): boolean => {
  const trimmed = value.trim();
  if (!trimmed) {
    return false;
  }
  if (NUMERIC_PATTERN.test(trimmed)) {
    return true;
  }
  return NUMERIC_WITH_WRAPPERS_PATTERN.test(trimmed);
};

export const isLikelyUrl = (value: string): boolean => {
  const trimmed = value.trim();
  if (!trimmed) {
    return false;
  }

  if (/^(https?:)?\/\//i.test(trimmed)) {
    return true;
  }

  if (trimmed.startsWith('/') && /[/?=&%]/.test(trimmed)) {
    return true;
  }

  if (trimmed.includes('://')) {
    return true;
  }

  return DOMAIN_PATTERN.test(trimmed);
};

export const isImageColumnType = (columnType?: string | null): boolean =>
  typeof columnType === 'string' && columnType.trim().toLowerCase() === 'image';

export const classifyCellValue = (raw: unknown, column: string, columnType?: string | null): PreviewCellKind => {
  if (isImageColumnType(columnType)) {
    const normalizedImage = normalizeImageUrl(raw);
    if (normalizedImage) {
      return 'image';
    }
  }

  if (raw === null || raw === undefined) {
    return 'empty';
  }

  if (typeof raw === 'number') {
    return 'number';
  }

  if (typeof raw === 'string') {
    const trimmed = raw.trim();
    if (!trimmed) {
      return 'empty';
    }
    if (URL_COLUMN_PATTERN.test(column) || isLikelyUrl(trimmed)) {
      return 'url';
    }
    if (isNumericString(trimmed)) {
      return 'number';
    }
    return 'text';
  }

  const formatted = formatTableCellValue(raw);
  if (formatted === '—') {
    return 'empty';
  }

  if (URL_COLUMN_PATTERN.test(column) || isLikelyUrl(formatted)) {
    return 'url';
  }

  if (isNumericString(formatted)) {
    return 'number';
  }

  return 'text';
};

export const toCompactDisplay = (raw: unknown): string => {
  const display = formatTableCellValue(raw);
  if (display === '—') {
    return display;
  }

  if (typeof raw === 'string' && isLikelyUrl(raw)) {
    return compactUrlValue(raw);
  }

  if (display.length > COMPACT_TEXT_MAX_LENGTH) {
    return `${display.slice(0, COMPACT_TEXT_MAX_LENGTH - 1)}…`;
  }

  return display;
};

export const isExpandable = (raw: unknown): boolean => {
  const display = formatTableCellValue(raw);
  if (display === '—') {
    return false;
  }

  if (isLikelyUrl(display)) {
    return display.length > EXPAND_URL_MIN_LENGTH;
  }

  if (display.length >= EXPAND_TEXT_MIN_LENGTH) {
    return true;
  }

  return display.split(/\s+/).some((segment) => segment.length >= EXPAND_UNBROKEN_TOKEN_MIN_LENGTH);
};
